/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import {
  MutableRefObject,
  useEffect,
  useMemo,
} from 'react';
import {
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import classNames from 'classnames';
import lodashRange from 'lodash/range';

import * as K from 'common/constants';
import { LogEntry, QueryType } from 'common/graphqlTypes';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { getRandomNumber } from 'common/util/number';
import { getRequestIds } from 'common/util/lrqRequest';

import { settingsState } from 'components/common/Managers/SettingsManager.state';

import LogResults from 'components/search/SearchLogsPage/LogResults';
import WrappedLogResults from 'components/search/SearchLogsPage/WrappedLogResults/WrappedLogResults';
import NoLogResults from 'components/search/SearchLogsPage/NoLogResults';
import { windowHeightState } from 'components/common/Managers/LayoutStateManager';

import {
  logResultsStatus,
  paginatedLogRequestsState,
  searchPageStatusSelector,
  useProgressiveStateManagement,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import { SeverityIcon } from 'components/search/LogLine';

import EventsContainer from 'components/search/SearchLogsPage/EventsContainer';
import TableLogResults from 'components/search/SearchLogsPage/TableLogResults/TableLogResults';

// TODO: Hardcoded component measurements are not reliable {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4039}
const NAVBAR_HEIGHT = 56;
const SEARCH_TOOLBAR_HEIGHT = 56;
const LOG_RESULTS_TOOLBAR_HEIGHT = 49;
const LOG_RESULTS_TIMELINE_CONTAINER_HEIGHT = 138;

type LogResultsContainerProps = {
  searchPanelHeight?: number;
  timelineHeight?: number;
  sidebarToggleRef: MutableRefObject<HTMLDivElement | null>;
  logDetailsRef?: MutableRefObject<HTMLDivElement | null>;
};

// #region LogResultsSkeleton
const tokens = [<div key={0} style={{ height: 10, width: 90 }} className="LogResults__Skeleton__Item" />,
  <div key={1} style={{ height: 10, width: 120 }} className="LogResults__Skeleton__Item" />,
  <div key={2} style={{ height: 10, width: 100 }} className="LogResults__Skeleton__Item" />,
  <div key={3} style={{ height: 10, width: 80 }} className="LogResults__Skeleton__Item" />,
  <div key={4} style={{ height: 10, width: 120 }} className="LogResults__Skeleton__Item" />,
  <div key={5} style={{ height: 10, width: 80 }} className="LogResults__Skeleton__Item" />,
  <div key={6} style={{ height: 10, width: 100 }} className="LogResults__Skeleton__Item" />,
  <div key={7} style={{ height: 10, width: 60 }} className="LogResults__Skeleton__Item" />,
  <div key={8} style={{ height: 10, width: 60 }} className="LogResults__Skeleton__Item" />,
  <div key={9} style={{ height: 10, width: 360 }} className="LogResults__Skeleton__Item" />,
  <div key={10} style={{ height: 10, width: 100 }} className="LogResults__Skeleton__Item" />,
  <div key={11} style={{ height: 10, width: 60 }} className="LogResults__Skeleton__Item" />,
  <div key={12} style={{ height: 10, width: 60 }} className="LogResults__Skeleton__Item" />];

/**
 * This component acts as a placeholder while the data is being loaded
 */
function LogResultsSkeleton(): JSX.Element {
  const logLinesData = useMemo(() => [...Array(50)].map(() => getRandomNumber(2, tokens.length - 1)), []);

  // The idea is to show a random amount of tokens for each row, making logLines look more like the real data
  const logLines = logLinesData.map((tokensSize, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <div key={index} className="LogResults__Skeleton__LogLine">
      <SeverityIcon severityLevel={1} isCellDisplay={false} />
      {lodashRange(tokensSize).map((tokenIndex) => tokens[tokenIndex])}
    </div>
  ));

  return (
    <div className="LogResults__Skeleton__Container skeleton-opacity-animation">
      {logLines}
    </div>
  );
}
// #endregion

/**
 * This is a wrapper to conditionally render different scroller implementations. In a real solution, those
 * implementations will share the same library and some state.
 *
 * @prop searchPanelHeight    combined height of search and results toolbars for non-standard page
 * @prop timelineHeight       height of timeline for non-standard page
 * @prop sidebarToggleRef     used to detect outside mousedown for closing inspect drawer
 * @prop logDetailsRef        used to detect outside mousedown for closing inspect drawer
 */
function LogResultsContainer({
  searchPanelHeight,
  timelineHeight,
  sidebarToggleRef,
  logDetailsRef,
}: LogResultsContainerProps): JSX.Element {

  const settings = useRecoilValue(settingsState);
  const windowHeight = useRecoilValue(windowHeightState);
  const setAllSearchPageStatuses = useSetRecoilState(searchPageStatusSelector);

  // We need to:
  // 1. get logEntryRequests from global state
  // 2. read merged data of all logEntryRequests from the cache
  // 3. feed merged data into a virtualized list
  //
  // TODO: FRONT-3851
  const logEntryRequests = useRecoilValue(paginatedLogRequestsState);
  const eventCacheIds = getRequestIds(logEntryRequests, QueryType.LOG);
  let eventCache = useEventCache(eventCacheIds, true);

  const allDone = eventCache.results?.length && eventCache.results?.every((result) => result.isDone);
  const noResults = allDone && eventCache.paginatedData.currentIds?.length === 0;
  const isLoading = !allDone && eventCache.paginatedData.currentIds?.length === 0;
  const returnPreviousCache = eventCache.results?.length === 0 || false;

  eventCache = useProgressiveStateManagement(eventCache, eventCacheIds, logResultsStatus, returnPreviousCache);
  const { paginatedData: { currentIds } } = eventCache;
  const isPreviousDataAvailable = eventCache.paginatedData.currentIds?.length > 0; // to know when to show skeleton UI

  const panelHeight = searchPanelHeight || (SEARCH_TOOLBAR_HEIGHT + LOG_RESULTS_TOOLBAR_HEIGHT);
  const heightOffset = NAVBAR_HEIGHT + panelHeight + (timelineHeight || LOG_RESULTS_TIMELINE_CONTAINER_HEIGHT);

  /**
   * Handle component statuses
   */
  useEffect(() => {
    if (noResults) {
      // @ts-expect-error
      setAllSearchPageStatuses(K.COMPONENT_STATUS.RESOLVED);
    }
  }, [noResults, setAllSearchPageStatuses]);

  if (noResults) {
    return <NoLogResults />;
  }

  if (isLoading && !isPreviousDataAvailable) {
    return <LogResultsSkeleton />;
  }

  let logView = (
    <LogResults
      logIds={currentIds}
      heightOffset={heightOffset}
    />
  );
  if (settings[K.SETTING.DISPLAY_MODE] === K.DISPLAY_MODE.WRAPPED) {
    logView = (
      <WrappedLogResults
        height={windowHeight - heightOffset}
        logIds={currentIds}
      />
    );
  }
  if (settings[K.SETTING.DISPLAY_MODE] === K.DISPLAY_MODE.TABLE) {
    logView = (
      <TableLogResults
        logIds={currentIds}
      />
    );
  }

  const getEventByToken = (token: string): LogEntry | null => {
    const logEntryEdge = token ? eventCache.getLogEntryEdge(token) : null;
    return logEntryEdge?.logEntry || null;
  };

  const getTokenByOffset = (token: string, offset: number): string => {
    let selectedIdx = token ? currentIds.indexOf(token) : -1;
    let newToken = '';
    if (selectedIdx !== -1) {
      selectedIdx += offset;
      newToken = currentIds[selectedIdx];
    }
    return newToken || '';
  };

  const outsideRefs = [ sidebarToggleRef ];
  if (logDetailsRef) {
    outsideRefs.push(logDetailsRef);
  }

  return (
    <EventsContainer
      eventsView={logView}
      getEventByToken={getEventByToken}
      getTokenByOffset={getTokenByOffset}
      outerClassName="LogResults"
      innerClassName={classNames(
        'LogResults__Wrapper',
        { 'dimming-overlay-effect': returnPreviousCache },
        { LogResults__WrapperOverFlow: settings[K.SETTING.DISPLAY_MODE] === K.DISPLAY_MODE.LINES },
      )}
      outsideRefs={outsideRefs}
    />
  );
}

export default LogResultsContainer;
